import React, { useState, useEffect } from 'react';
import { getMatchUpdateLogs } from '../services/matchUpdateLogService';
import { LOG_TYPES } from '../services/matchUpdateLogService';
import { formatDateTime } from '../utils/dateFormat';
import './MatchUpdateHistoryModal.css';

const TYPE_LABELS = {
  [LOG_TYPES.CREATED]: 'Match created',
  [LOG_TYPES.SCHEDULED]: 'Scheduled',
  [LOG_TYPES.RESCHEDULE_REQUESTED]: 'Reschedule requested',
  [LOG_TYPES.RESCHEDULE_APPROVED]: 'Reschedule approved',
  [LOG_TYPES.RESCHEDULE_COMPLETED]: 'Reschedule completed',
  [LOG_TYPES.STARTED]: 'Match started',
  [LOG_TYPES.REFEREE_CHANGED]: 'Referee changed',
  [LOG_TYPES.COMPLETED]: 'Match completed'
};

const TYPE_ICONS = {
  [LOG_TYPES.CREATED]: '📋',
  [LOG_TYPES.SCHEDULED]: '📅',
  [LOG_TYPES.RESCHEDULE_REQUESTED]: '🔄',
  [LOG_TYPES.RESCHEDULE_APPROVED]: '✓',
  [LOG_TYPES.RESCHEDULE_COMPLETED]: '📅',
  [LOG_TYPES.STARTED]: '▶️',
  [LOG_TYPES.REFEREE_CHANGED]: '👤',
  [LOG_TYPES.COMPLETED]: '🏁'
};

function formatLogDetail(log) {
  const name = log.performedByName || 'Someone';
  switch (log.type) {
    case LOG_TYPES.RESCHEDULE_REQUESTED:
      return log.requestedFor
        ? `${name} requested reschedule to ${log.requestedFor}`
        : `${name} requested a reschedule`;
    case LOG_TYPES.RESCHEDULE_APPROVED:
      return `${name} approved the reschedule`;
    case LOG_TYPES.RESCHEDULE_COMPLETED:
      return log.oldDate && log.newDate
        ? `${name} rescheduled from ${log.oldDate} to ${log.newDate}`
        : `${name} completed the reschedule`;
    case LOG_TYPES.STARTED:
      return `${name} started the match as referee`;
    case LOG_TYPES.REFEREE_CHANGED:
      return log.oldReferee && log.newReferee
        ? `${name} changed referee from ${log.oldReferee} to ${log.newReferee}`
        : `${name} changed the referee`;
    case LOG_TYPES.COMPLETED:
      return log.scoreSummary
        ? `${name} completed the match (${log.scoreSummary})`
        : `${name} completed the match`;
    case LOG_TYPES.SCHEDULED:
      return log.newDate ? `Scheduled for ${log.newDate}` : 'Match was scheduled';
    default:
      return log.performedByName ? `By ${log.performedByName}` : null;
  }
}

export default function MatchUpdateHistoryModal({ matchId, onClose }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!matchId) return;
    let cancelled = false;
    getMatchUpdateLogs(matchId).then((list) => {
      if (!cancelled) {
        setLogs(list);
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [matchId]);

  const formatTimestamp = (ts) => (ts ? formatDateTime(ts) : '');

  return (
    <div className="match-update-history-overlay" onClick={onClose}>
      <div className="match-update-history-modal" onClick={(e) => e.stopPropagation()}>
        <div className="match-update-history-header">
          <h2>Match update history</h2>
          <button type="button" className="match-update-history-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="match-update-history-content">
          {loading ? (
            <p className="match-update-history-loading">Loading...</p>
          ) : logs.length === 0 ? (
            <p className="match-update-history-empty">No update history yet for this match.</p>
          ) : (
            <ul className="match-update-history-list">
              {logs.map((log) => (
                <li key={log.id} className={`match-update-history-item type-${log.type}`}>
                  <span className="match-update-history-icon">{TYPE_ICONS[log.type] || '•'}</span>
                  <div className="match-update-history-details">
                    <div className="match-update-history-title">{TYPE_LABELS[log.type] || log.type}</div>
                    <div className="match-update-history-desc">{formatLogDetail(log)}</div>
                    <div className="match-update-history-time">{formatTimestamp(log.performedAt)}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
